<template>
    <div class="container-fluid px-0">
        <!-- Breadcrumbs (Large screens) -->
        <nav aria-label="breadcrumb" class="d-none d-md-flex mt-3" v-if="specific_data">
            <ol class="breadcrumb">
                <li class="breadcrumb-item">
                    <router-link to="/profile" class="text-decoration-none" v-if="(auth.is_member || auth.is_admin || auth.is_moderator) && specific_data.user_id == auth.user.id">
                        My Surveys
                    </router-link>
                    <router-link to="/admin" class="text-decoration-none" v-else-if="auth.is_admin || auth.is_moderator">
                        Admin
                    </router-link>
                </li>
                <li class="breadcrumb-item active" aria-current="page">Survey Response</li>
            </ol>
        </nav>

        <!-- Title  (Large screens) -->
        <h2 class="d-none d-md-block fw-bold text-start mb-large">Survey Response</h2>

        <!-- Nav (Small screens) -->
        <div class="d-md-none d-flex align-items-center justify-content-between small-nav">
            <!-- Back icon -->
            <a v-load-page="'/'" v-ripple class="navbar-brand fw-bold fs-4 py-0 m-0 rounded-circle">
                <i class="hoverable-icon bi-arrow-left"></i>
            </a>
            <!-- Title -->
            <p class="fs-5 fw-bold m-0">Survey Response</p>
            <!-- Menu toggler -->
            <i class="hoverable-icon bi-three-dots-vertical" v-ripple></i>
        </div>
    </div>

    <div class="divider sm mt-3 d-none d-md-block"></div>

    <div class="container-fluid px-0 my-3" ref="surveyItems" style="min-height: 10rem;">
        <div class="row row-cols-1 row-cols-md-2 g-3" v-if="specific_data">
            <div class="col d-grid" v-for="(question, index) in questions.filter(q => q.answers && (q.answers.length > 1 || (q.answers.length === 1 && q.answers[0].value !== '')))">
                <SurveyResponseCard :question="question" :number="index + 1" :description="generateDescription(question.desc)" :survey="specific_data"></SurveyResponseCard>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import SurveyResponseCard from '@/components/SurveyCard/SurveyResponseCard.vue';
import { useAuthStore } from "@/stores/auth-store";
import apiClient from '@/utils/axios';
import { useHttpErrorManager } from '@/composables/useHttpErrorManager';
import { conversionFactor } from '@/utils/conversionFactors.js';
import { useAppHelpers } from '@/composables/useAppHelpers';

const auth = useAuthStore();
const helper = useAppHelpers();

const props = defineProps({
    id: String
});

const surveyItems = ref(null);

const specific_data = ref(null);
const name = ref('');
const id = ref(null);
const medication = ref(null);
const condition_therapy = ref(null);
const minor_conditions = ref(null);
const minor_therapies = ref(null);
const other_medications_past = ref(null);
const other_medications_now = ref(null);
const all_side_effects = ref(null);
const early_side_effects = ref(null);
const other_side_effects = ref(null);
const current_side_effects = ref(null);
const forgotten_side_effects = ref(null);
const all_symptoms = ref(null);
const early_symptoms = ref(null);
const other_symptoms = ref(null);
const current_symptoms = ref(null);
const activities = ref(null);
const questions = ref([]);

const weight_height_representation = (value, type) => {
    let convertedVal = "";

    if (type === "weight_unit") {
        convertedVal = value / conversionFactor['pound'];
        convertedVal = Math.round(convertedVal * 10) / 10;
        convertedVal += convertedVal <= 1 ? " lb" : " lbs";
    } else if (type === "height_unit") {
        const convertedFeet = value / conversionFactor['foot'];
        const feets = Math.floor(convertedFeet);
        const remainingInches = Math.round((convertedFeet - feets) * 12 * 10) / 10;
        convertedVal = `${feets}' ${remainingInches}"`;
    }

    return convertedVal;
};

const getUnit = (numericValue, tooltipLabels) => {
    let [unit, divisor] = ['sec', 1];

    for (const [key, value] of Object.entries(tooltipLabels)) {
        const numericKey = parseInt(key, 10);

        if (numericValue >= numericKey) {
            [unit, divisor] = [value, numericKey];
        } else {
            break;
        }
    }

    const val = Math.floor(numericValue / divisor);
    unit = val > 1 ? unit + 's' : unit;

    return [`${val} ${unit} `, val, divisor];
};

const number_to_time = (nm) => {
    const init = nm;
    const tooltipLabels = {
        1: 'second',
        60: 'minute',
        3600: 'hour',
        86400: 'day',
        2592000: 'month',
        31104000: 'year',
    };

    let tooltip = '';

    while (nm > 0) {
        if (nm % 10 === 9) {
            nm += 1;
        }
        if (nm % 10 === 1) {
            nm -= 1;
        }

        const [unitString, val, divisor] = getUnit(nm, tooltipLabels);
        tooltip += unitString;
        nm -= val * parseFloat(divisor);
        nm = Math.floor(nm);

        if ((init > 86400 && nm < 86400) || (init > 60 && nm < 60)) {
            nm = 0;
        }
    }

    return tooltip;
};

const updateData = (data) => {
    specific_data.value = data.survey;
    name.value = data.name;
    id.value = data.survey.id;
    medication.value = data.med_data;
	condition_therapy.value = data.cond_therapy_data;
    minor_conditions.value = data.minor_cond_data;
    minor_therapies.value = data.minor_therapy_data;
    other_medications_past.value = data.other_medications_past;
    other_medications_now.value = data.other_medications_now;
    all_side_effects.value = data.all_side_effects_data;
    early_side_effects.value = data.early_side_effects_data;
    other_side_effects.value = data.other_side_effects_data;
    current_side_effects.value = data.current_side_effects_data;
    forgotten_side_effects.value = data.forgotten_side_effects_data;
    all_symptoms.value = data.all_symptoms_data;
    early_symptoms.value = data.early_symptoms_data;
    other_symptoms.value = data.other_symptoms_data;
    current_symptoms.value = data.current_symptoms_data;
    activities.value = data.activities_data;

    data.questions.map(item => {
        const id = item.id;
        const order_num = item.order_num;
        const ind = questions.value.findIndex((v) => v.id === id);

		let desc = item.description;
		let sub = item.sub_text;
		if(specific_data.value.type == 2){
			desc = item.description_past ?? item.description;
			sub = item.sub_text_past ?? item.sub_text;
		}

	    questions.value.push({
		    id: id,
		    order_num: order_num,
		    desc: desc,
		    sub: sub,
		    answers: item.pivot.answer,
		    variable_name: item.variable_name,
		    questionType: item.question_type
	    });
    });

};

const get_data = async () => {
    if (props.id) {
        helper.loaderOn(surveyItems.value);
        const url = `/member/surveys/data/${props.id}`;
        try {
            const response = await apiClient.get(url, {});
            if (response.status === 200) {
                updateData(response.data.data);
                
            } else {
                specific_data.value = null;
            }
            helper.loaderOff(surveyItems.value);
        } catch (error) {
            let errorObj = await useHttpErrorManager().handleError(error, false);
            helper.loaderOff(surveyItems.value);
        }
    }
};

const generateDescription = (val) => {
    if (!val) {
        return "";
    }

	console.log('all_symptoms.value ', all_symptoms.value);

    const medName = medication.value?.[1] ? `<b>${medication.value[1]}</b>` : "";
    const conditionTherapyName = getDescriptionHelper(condition_therapy.value);

    const otherMedicationPastText = getDescriptionHelper(other_medications_past.value);
    const otherMedicationNowText = getDescriptionHelper(other_medications_now.value);
    const earlySymptomsText = getDescriptionHelper(early_symptoms.value);
    const allSymptomsText = getDescriptionHelper(all_symptoms.value);
    const currentSymptomsText = getDescriptionHelper(current_symptoms.value);
    const otherSymptomsText = getDescriptionHelper(other_symptoms.value);
    const earlySideEffectsText = getDescriptionHelper(early_side_effects.value);
    const currentSideEffectsText = getDescriptionHelper(current_side_effects.value);
    const allSideEffectsText = getDescriptionHelper(all_side_effects.value);
    const forgottenSideEffectsText = getDescriptionHelper(forgotten_side_effects.value);
    const otherSideEffectsText = getDescriptionHelper(other_side_effects.value);
    const activitiesText = getDescriptionHelper(activities.value);

    const replacements = [
        { key: "[medication]", value: medName },
        { key: "[Medication]", value: medName },
        { key: "[MEDICATION]", value: medName },
        { key: "[condition_therapy]", value: conditionTherapyName },
        { key: "[Condition_Therapy]", value: conditionTherapyName },
        { key: "[CONDITION_THERAPY]", value: conditionTherapyName }
    ];

    let updatedVal = val;
    updatedVal = applyReplacements(updatedVal, replacements);

    updatedVal = descriptionHelper2(updatedVal, otherMedicationPastText, '[other_medications_past]');
    updatedVal = descriptionHelper2(updatedVal, otherMedicationPastText, '[*other_medications_past*]');
    updatedVal = descriptionHelper2(updatedVal, otherMedicationNowText, '[other_medications_now]');
    updatedVal = descriptionHelper2(updatedVal, otherMedicationNowText, '[*other_medications_now*]');
    updatedVal = descriptionHelper2(updatedVal, earlySymptomsText, '[early_symptoms]');
    updatedVal = descriptionHelper2(updatedVal, earlySymptomsText, '[*early_symptoms*]');
    updatedVal = descriptionHelper2(updatedVal, currentSymptomsText, '[current_symptoms]');
    updatedVal = descriptionHelper2(updatedVal, currentSymptomsText, '[*current_symptoms*]');
    updatedVal = descriptionHelper2(updatedVal, otherSymptomsText, '[other_symptoms]');
    updatedVal = descriptionHelper2(updatedVal, otherSymptomsText, '[*other_symptoms*]');
    updatedVal = descriptionHelper2(updatedVal, allSymptomsText, '[all_symptoms]');
    updatedVal = descriptionHelper2(updatedVal, allSymptomsText, '[*all_symptoms*]');
    updatedVal = descriptionHelper2(updatedVal, earlySideEffectsText, '[early_side_effects]');
    updatedVal = descriptionHelper2(updatedVal, earlySideEffectsText, '[*early_side_effects*]');
    updatedVal = descriptionHelper2(updatedVal, currentSideEffectsText, '[current_side_effects]');
    updatedVal = descriptionHelper2(updatedVal, currentSideEffectsText, '[*current_side_effects*]');
    updatedVal = descriptionHelper2(updatedVal, allSideEffectsText, '[all_side_effects]');
    updatedVal = descriptionHelper2(updatedVal, allSideEffectsText, '[*all_side_effects*]');
    updatedVal = descriptionHelper2(updatedVal, forgottenSideEffectsText, '[forgotten_side_effects]');
    updatedVal = descriptionHelper2(updatedVal, forgottenSideEffectsText, '[*forgotten_side_effects*]');
    updatedVal = descriptionHelper2(updatedVal, otherSideEffectsText, '[other_side_effects]');
    updatedVal = descriptionHelper2(updatedVal, otherSideEffectsText, '[*other_side_effects*]');
    updatedVal = descriptionHelper2(updatedVal, activitiesText, '[activities]');
    updatedVal = descriptionHelper2(updatedVal, activitiesText, '[*activities*]');

    updatedVal = updatedVal.replaceAll("[info]", "<small class='d-block alert alert-warning my-2' style='font-weight: 400 !important; font-size: 13px;'>");
    updatedVal = updatedVal.replaceAll("[/info]", "</small>");

    updatedVal = removeDisplayPlaceholders(updatedVal);

    updatedVal = updatedVal.replace(/^<p>/, '');
    updatedVal = updatedVal.replace(/<\/p>$/, '');

    return updatedVal;
};

const getDescriptionHelper = (variable, additionalVariable) => {
    if (!variable || variable.length === 0) {
        return "";
    }

    let name = variable.map(item => item.text).join(", ");

    if (variable.length > 1) {
        name = name.replace(/,([^,]*)$/, " and$1");
    }

    if (additionalVariable && additionalVariable.length > 0) {
        name += " " + getDescriptionHelper(additionalVariable);
    }

    return `<b>${name}</b>`;
};

const combineDescriptions = (desc1, desc2) => {
    return (desc1 && desc2) ? desc1 + " " + desc2 : (desc1 || desc2)
};

const descriptionHelper2 = (val, name, replace) => {
    if (name) {
        name = "<b>" + name + "</b>";
        val = val.replaceAll(replace, name);
    }
    return val;
};

const applyReplacements = (val, replacements) => {
    replacements.forEach(({ key, value }) => {
        if (value) {
            val = val.replaceAll(key, value);
            val = val.replaceAll(key.toUpperCase(), value);
            val = val.replaceAll(key.toLowerCase(), value);
        }
    });
    return val;
};

const removeDisplayPlaceholders = (val) => {
    const placeholdersToRemove = [
        "[display_medication]", "[display_all_medications]", "[display_conditions]",
        "[display_therapies]", "[display_conditions_therapies]", "[display_side_effects]",
        "[display_symptoms]", "[display_current_side_effects]", "[display_current_symptoms]",
        "[display_early_side_effects]", "[display_early_symptoms]", "[display_other_side_effects]",
        "[display_other_symptoms]", "[display_other_medications_past]", "[display_other_medications_now]",
        "[display_main_condition]", "[display_minor_conditions]", "[display_main_therapy]",
        "[display_minor_therapies]"
    ];

    placeholdersToRemove.forEach(placeholder => {
        val = val.replaceAll(placeholder, "");
    });

    return val;
};


onMounted(() => {
    get_data();
});


</script>
  